import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useApp, API } from "../context";
import { SparkleField } from "../components/shared";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { getPostLoginLanding } from "../utils/postLoginLanding";
import { consumeOAuthNext } from "../utils/oauthNext";

// Read any already-established session directly from storage. A logged-in user
// who lands here with a stale/rejected OAuth code should NOT see a scary
// "Sign-in failed" screen, they're already in, so we bounce them into the app.
function readStoredSession() {
  try {
    const token = localStorage.getItem("isaiah_token") || sessionStorage.getItem("isaiah_token");
    const rawUser = localStorage.getItem("isaiah_user") || sessionStorage.getItem("isaiah_user");
    return { token: token || null, user: rawUser ? JSON.parse(rawUser) : null };
  } catch (_e) {
    return { token: null, user: null };
  }
}

/**
 * AuthCallback, handles the Google OAuth redirect from either:
 *   - Isaiah's World custom OAuth (current): `?code=...&state=...` → POST to /auth/google/custom-exchange
 *   - Legacy Emergent-managed OAuth: `#session_id=...` → POST to /auth/google/session
 * The legacy path is kept temporarily so any user mid-flight when we cut
 * over still completes their sign-in. New sessions use the custom path.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useApp();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState("loading");
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Custom OAuth (current): ?code=...&state=... in query string
    const search = new URLSearchParams(window.location.search);
    const code = search.get("code");
    const state = search.get("state");
    const googleError = search.get("error");

    // Legacy Emergent OAuth: #session_id=... in URL fragment
    const hash = window.location.hash || "";
    const sessionId = new URLSearchParams(hash.replace("#", "?")).get("session_id");

    // If a valid session already exists (e.g. the user refreshed the callback
    // URL, or a stale code got replayed) just take them into the app instead
    // of surfacing a confusing error while they're clearly signed in.
    const bounceIfAlreadySignedIn = () => {
      const { token, user: storedUser } = readStoredSession();
      if (token && storedUser) {
        window.history.replaceState(null, "", "/");
        navigate(getPostLoginLanding(storedUser, { needsCompletion: false }), { replace: true });
        return true;
      }
      return false;
    };

    if (googleError) {
      if (bounceIfAlreadySignedIn()) return;
      setStatus("error");
      setErrMsg(googleError === "access_denied"
        ? "You canceled the Google sign-in. No worries, you can try again anytime."
        : "We couldn't finish signing you in. Please try again.");
      return;
    }

    if (!code && !sessionId) {
      if (bounceIfAlreadySignedIn()) return;
      setStatus("error");
      setErrMsg("No sign-in data found in the URL. Please try signing in again.");
      return;
    }

    (async () => {
      try {
        const doExchange = () => {
          if (code) {
            // Custom Isaiah's World OAuth flow
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUri = window.location.origin + "/auth/google";
            let signupType = "";
            let signupTeenGrade = "";
            try {
              signupType = sessionStorage.getItem("isaiah_signup_account_type") || "";
              signupTeenGrade = sessionStorage.getItem("isaiah_signup_teen_grade") || "";
            } catch (_) {}
            return axios.post(`${API}/auth/google/custom-exchange`, {
              code,
              state,
              redirect_uri: redirectUri,
              account_type: signupType || undefined,
              grade: signupTeenGrade || undefined,
            }, { timeout: 30000 });
          }
          // Legacy Emergent-managed flow (kept for transition period only)
          return axios.post(`${API}/auth/google/session`, { session_id: sessionId }, { timeout: 30000 });
        };

        // First attempt; on failure retry ONCE after a short pause. A slow or
        // duplicated first attempt on mobile can leave the single-use code
        // consumed but the session already built server-side, the backend then
        // returns that cached session on retry instead of a false failure.
        let res;
        try {
          res = await doExchange();
        } catch (firstErr) {
          console.warn("Google exchange attempt 1 failed, retrying once:", firstErr?.message);
          await new Promise((r) => setTimeout(r, 1500));
          res = await doExchange();
        }
        const { token, user } = res.data || {};
        if (!token || !user) {
          setStatus("error");
          setErrMsg("Sign-in response was incomplete.");
          return;
        }
        if (user.is_new_account) sessionStorage.setItem("isaiah_just_signed_up", "true");
        login(token, user, true);

        // Claim any cached guest preview progress
        try {
          const raw = localStorage.getItem("isaiah_guest_progress");
          if (raw) {
            const p = JSON.parse(raw);
            const claim = await axios.post(`${API}/guest/claim-progress`, {
              user_id: user.id,
              stars: p.stars || 0,
              games_played: p.games_played || 0,
              lessons_started: p.lessons_started || 0,
              extensions_used: p.extensions_used || 0,
              started_at: p.started_at,
              activity_log: p.activity_log || [],
            }).catch(() => null);
            localStorage.removeItem("isaiah_guest_progress");
            if (claim?.data?.stars_added > 0) {
              toast.success(`You kept your preview progress! +${claim.data.stars_added} stars carried over.`, { duration: 5000 });
            }
          }
        } catch (_) {}

        setStatus("success");
        const needsCompletion = !!(user.needs_profile_completion || user.is_new_account);
        toast.success(user.is_new_account ? "Welcome to Isaiah's World!" : "Welcome back!", {
          description: needsCompletion
            ? "Let's finish setting up your Explorer profile."
            : (user.is_new_account ? "Your Google-powered Explorer account is ready." : "You're signed in."),
        });
        window.history.replaceState(null, "", window.location.pathname);
        // Role-aware landing, matches the email/password quick-login flow
        // in Header.js so Google sign-in lands admins on the admin dashboard
        // and parents on Parent Corner instead of the kid-side My World.
        // When the user landed here from a deep-link (e.g. /admin-dashboard
        // → /login?next=%2Fadmin-dashboard → Google), GoogleSignInButton /
        // LoginPage stashed the next path in sessionStorage before redirecting
        // to Google. Consume it now so they land on the page they originally
        // requested, but only when the account is fully set up. Brand-new
        // accounts must complete their profile first; we keep the stashed
        // value untouched so /complete-profile can pick it up afterwards.
        let landing = getPostLoginLanding(user, { needsCompletion });
        if (!needsCompletion) {
          const nextDest = consumeOAuthNext();
          if (nextDest) landing = nextDest;
        }
        setTimeout(() => navigate(landing, { replace: true }), 800);
      } catch (err) {
        console.error("Google auth failed:", err);
        // Already signed in? A rejected/replayed code is harmless, take them in.
        if (bounceIfAlreadySignedIn()) return;
        setStatus("error");
        setErrMsg("We couldn't finish signing you in. Please try again.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page-universe auth-page" data-testid="auth-callback">
      <SparkleField count={16} />
      <div className="auth-card max-w-sm text-center">
        {status === "loading" && (
          <>
            <Loader2 size={36} className="mx-auto text-purple-300 animate-spin mb-4" />
            <h1 className="text-lg font-fredoka font-bold text-white mb-1">Signing you in…</h1>
            <p className="text-white/50 text-xs">This only takes a second.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle size={40} className="mx-auto text-emerald-300 mb-3" />
            <h1 className="text-lg font-fredoka font-bold text-white mb-1">You're in!</h1>
            <p className="text-white/50 text-xs">Taking you to your dashboard…</p>
          </>
        )}
        {status === "error" && (
          <>
            <AlertCircle size={40} className="mx-auto text-red-300 mb-3" />
            <h1 className="text-lg font-fredoka font-bold text-white mb-1">Sign-in failed</h1>
            <p className="text-white/55 text-xs mb-4">{errMsg}</p>
            <button onClick={() => navigate("/login")}
              className="px-5 py-2 rounded-xl bg-purple-500/20 border border-purple-400/30 text-purple-100 text-sm font-bold"
              data-testid="auth-callback-retry">Back to Login</button>
          </>
        )}
      </div>
    </div>
  );
}
